/**
 * Shared poster URL helpers for WOM / theme videos.
 *
 * Rules:
 * - Prefer the API `thumbnail` URL (…_thumb.jpg) when present.
 * - Fall back to other explicit still fields / product image.
 * - Derived sibling .jpg/.png URLs are only used after a successful image load
 *   probe — never paint a broken-image icon.
 * - Missing posters leave the video alone.
 */
(function () {
  'use strict';

  if (window.WOMVideoPoster) return;

  var VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v|ogg)(?=([?#]|$))/i;
  var probeCache = {}; // url -> true (ok) | false (failed)

  function stripToPath(url) {
    if (!url || typeof url !== 'string') return '';
    var q = url.indexOf('?');
    var h = url.indexOf('#');
    var end = url.length;
    if (q !== -1) end = Math.min(end, q);
    if (h !== -1) end = Math.min(end, h);
    return url.slice(0, end);
  }

  function suffixOf(url) {
    if (!url || typeof url !== 'string') return '';
    var path = stripToPath(url);
    return url.slice(path.length);
  }

  function fromVideoUrl(url, ext) {
    if (!url || !VIDEO_EXT_RE.test(stripToPath(url))) return '';
    var path = stripToPath(url);
    var outExt = ext || 'jpg';
    return path.replace(VIDEO_EXT_RE, '.' + outExt) + suffixOf(url);
  }

  /** CDN convention: …/file.mp4 → …/file_thumb.jpg */
  function thumbFromVideoUrl(url, ext) {
    if (!url || !VIDEO_EXT_RE.test(stripToPath(url))) return '';
    var path = stripToPath(url);
    var outExt = ext || 'jpg';
    return path.replace(VIDEO_EXT_RE, '_thumb.' + outExt) + suffixOf(url);
  }

  function candidatesFromVideoUrl(url) {
    if (!url) return [];
    var list = [];
    var seen = {};
    function push(next) {
      if (next && !seen[next]) {
        seen[next] = true;
        list.push(next);
      }
    }
    // Prefer …_thumb.jpg — WOM CDN serves these; plain .jpg siblings are often 403.
    ['jpg', 'webp', 'jpeg', 'png'].forEach(function (ext) {
      push(thumbFromVideoUrl(url, ext));
    });
    ['jpg', 'jpeg', 'png', 'webp'].forEach(function (ext) {
      push(fromVideoUrl(url, ext));
    });
    return list;
  }

  function pickFromApi(v) {
    if (!v || typeof v !== 'object') return '';
    /* Prefer API thumbnail JPG (cdn …_thumb.jpg) over legacy poster fields */
    return (
      v.thumbnail ||
      v.thumbnail_url ||
      v.poster ||
      v.cover ||
      v.cover_image ||
      v.preview_image ||
      v.previewImage ||
      ''
    );
  }

  /**
   * Trusted sources only (no derived sibling guess). Safe to put on <img> immediately.
   * @param {object} opts
   */
  function resolveTrusted(opts) {
    opts = opts || {};
    if (opts.poster) return String(opts.poster);
    var api = pickFromApi(opts.video || opts.entry);
    if (api) return String(api);
    if (opts.fallback) return String(opts.fallback);
    return '';
  }

  /**
   * Includes derived sibling URL as last resort. Prefer probe() / loadInto()
   * before painting that URL on an <img>.
   */
  function resolve(opts) {
    opts = opts || {};
    // Prefer trusted; derived only if previously probed OK.
    var trusted = resolveTrusted(opts);
    if (trusted) return trusted;
    var derived = fromVideoUrl(opts.videoUrl || opts.url || '');
    if (derived && probeCache[derived] === true) return derived;
    return '';
  }

  function apply(videoEl, posterUrl) {
    if (!videoEl || !posterUrl) return false;
    try {
      videoEl.setAttribute('poster', posterUrl);
      return true;
    } catch (e) {
      return false;
    }
  }

  function applyResolved(videoEl, opts) {
    if (!videoEl) return '';
    // Native <video poster> does not show a broken-image icon on 404.
    var primary = resolveTrusted(opts);
    if (primary) {
      apply(videoEl, primary);
      return primary;
    }
    var derived = fromVideoUrl(opts && (opts.videoUrl || opts.url));
    if (derived) {
      apply(videoEl, derived);
      return derived;
    }
    return '';
  }

  function probe(url) {
    if (!url) return Promise.resolve(false);
    if (Object.prototype.hasOwnProperty.call(probeCache, url)) {
      return Promise.resolve(!!probeCache[url]);
    }
    return new Promise(function (resolveOk) {
      var img = new Image();
      var settled = false;
      function done(ok) {
        if (settled) return;
        settled = true;
        probeCache[url] = !!ok;
        resolveOk(!!ok);
      }
      img.onload = function () {
        done(img.naturalWidth > 0);
      };
      img.onerror = function () {
        done(false);
      };
      // Short timeout so slow 404s do not hang UI.
      setTimeout(function () {
        done(probeCache[url] === true);
      }, 4000);
      img.decoding = 'async';
      img.src = url;
    });
  }

  function clearImg(imgEl, hostEl, hostClass) {
    if (!imgEl) return;
    try {
      imgEl.removeAttribute('src');
      imgEl.removeAttribute('srcset');
    } catch (e0) {}
    imgEl.removeAttribute('data-poster-ready');
    imgEl.removeAttribute('data-poster-loading');
    imgEl.classList.remove('is-loaded');
    if (hostEl) {
      hostEl.classList.remove(hostClass || 'is-has-poster');
      hostEl.classList.remove('vg-card-outer--has-poster');
    }
  }

  /**
   * Load a poster into an <img> only after it succeeds.
   * Tries trusted URL first, then derived candidates. Never leaves a broken icon.
   *
   * @param {HTMLImageElement} imgEl
   * @param {object} opts same as resolve()
   * @param {HTMLElement} [hostEl] gets has-poster / vg-card-outer--has-poster on success
   * @returns {Promise<string>} loaded URL or ''
   */
  function loadInto(imgEl, opts, hostEl) {
    if (!imgEl) return Promise.resolve('');
    opts = opts || {};
    var hostClass = opts.hostClass || 'is-has-poster';

    var urls = [];
    var trusted = resolveTrusted(opts);
    if (trusted) urls.push(trusted);
    candidatesFromVideoUrl(opts.videoUrl || opts.url || '').forEach(function (u) {
      if (urls.indexOf(u) === -1) urls.push(u);
    });

    if (!urls.length) {
      clearImg(imgEl, hostEl, hostClass);
      return Promise.resolve('');
    }

    // Already showing this still — keep it painted (no hide → reload flash).
    var currentSrc = imgEl.getAttribute('src') || '';
    if (
      currentSrc &&
      urls.indexOf(currentSrc) !== -1 &&
      imgEl.complete &&
      imgEl.naturalWidth > 0
    ) {
      imgEl.setAttribute('data-poster-ready', '1');
      imgEl.removeAttribute('data-poster-loading');
      imgEl.classList.add('is-loaded');
      if (hostEl) {
        hostEl.classList.add(hostClass);
        hostEl.classList.add('vg-card-outer--has-poster');
      }
      probeCache[currentSrc] = true;
      return Promise.resolve(currentSrc);
    }

    imgEl.setAttribute('data-poster-loading', '1');
    // Do not clear is-loaded / data-poster-ready while a previous frame is
    // still on screen — only swap after the next URL succeeds.

    var i = 0;
    function tryNext() {
      if (i >= urls.length) {
        // Keep prior successful still if we already had one; otherwise clear.
        if (!(imgEl.complete && imgEl.naturalWidth > 0 && imgEl.getAttribute('src'))) {
          clearImg(imgEl, hostEl, hostClass);
        } else {
          imgEl.removeAttribute('data-poster-loading');
        }
        return Promise.resolve('');
      }
      var url = urls[i++];
      if (probeCache[url] === false) return tryNext();

      return probe(url).then(function (ok) {
        if (!ok || !imgEl.isConnected) return tryNext();

        return new Promise(function (resolveDone) {
          function succeed() {
            imgEl.setAttribute('data-poster-ready', '1');
            imgEl.removeAttribute('data-poster-loading');
            imgEl.classList.add('is-loaded');
            if (hostEl) {
              hostEl.classList.add(hostClass);
              if (hostEl.classList.contains('vg-card-outer') || hostEl.querySelector) {
                hostEl.classList.add('vg-card-outer--has-poster');
              }
            }
            resolveDone(url);
          }

          function fail() {
            probeCache[url] = false;
            tryNext().then(resolveDone);
          }

          if (imgEl.getAttribute('src') === url && imgEl.complete && imgEl.naturalWidth > 0) {
            succeed();
            return;
          }

          imgEl.onload = function () {
            imgEl.onload = null;
            imgEl.onerror = null;
            if (imgEl.naturalWidth > 0) succeed();
            else fail();
          };
          imgEl.onerror = function () {
            imgEl.onload = null;
            imgEl.onerror = null;
            fail();
          };
          imgEl.decoding = 'async';
          imgEl.src = url;
        });
      });
    }

    return tryNext();
  }

  /**
   * Paint a poster immediately (no probe wait). Shows the img while bytes load
   * so slow networks never sit on an empty placeholder. Falls back to loadInto()
   * if the URL 404s.
   */
  function paintEager(imgEl, opts, hostEl) {
    if (!imgEl) return Promise.resolve('');
    opts = opts || {};
    var hostClass = opts.hostClass || 'is-has-poster';

    var url = resolveTrusted(opts);
    if (!url) {
      var fromVideo = candidatesFromVideoUrl(opts.videoUrl || opts.url || '');
      url = fromVideo.length ? fromVideo[0] : '';
    }
    if (!url) return loadInto(imgEl, opts, hostEl);

    if (
      imgEl.getAttribute('src') === url &&
      imgEl.complete &&
      imgEl.naturalWidth > 0
    ) {
      imgEl.setAttribute('data-poster-ready', '1');
      imgEl.removeAttribute('data-poster-loading');
      imgEl.classList.add('is-loaded');
      if (hostEl) hostEl.classList.add(hostClass);
      probeCache[url] = true;
      return Promise.resolve(url);
    }

    if (hostEl) hostEl.classList.add(hostClass);
    imgEl.classList.add('is-loaded');
    imgEl.setAttribute('data-poster-ready', '1');
    imgEl.setAttribute('data-poster-loading', '1');

    return new Promise(function (resolveDone) {
      function cleanup() {
        imgEl.onload = null;
        imgEl.onerror = null;
      }

      function succeed() {
        cleanup();
        imgEl.removeAttribute('data-poster-loading');
        probeCache[url] = true;
        resolveDone(url);
      }

      function fail() {
        cleanup();
        probeCache[url] = false;
        imgEl.removeAttribute('data-poster-ready');
        imgEl.removeAttribute('data-poster-loading');
        imgEl.classList.remove('is-loaded');
        try {
          imgEl.removeAttribute('src');
        } catch (e0) {}
        if (hostEl) hostEl.classList.remove(hostClass);
        loadInto(imgEl, opts, hostEl).then(resolveDone);
      }

      imgEl.onload = function () {
        if (imgEl.naturalWidth > 0) succeed();
        else fail();
      };
      imgEl.onerror = fail;

      imgEl.decoding = 'async';
      if (imgEl.getAttribute('src') !== url) imgEl.src = url;
      else if (imgEl.complete) {
        if (imgEl.naturalWidth > 0) succeed();
        else fail();
      }
    });
  }

  window.WOMVideoPoster = {
    fromVideoUrl: fromVideoUrl,
    thumbFromVideoUrl: thumbFromVideoUrl,
    candidatesFromVideoUrl: candidatesFromVideoUrl,
    pickFromApi: pickFromApi,
    resolve: resolve,
    resolveTrusted: resolveTrusted,
    apply: apply,
    applyResolved: applyResolved,
    probe: probe,
    loadInto: loadInto,
    paintEager: paintEager,
    clearImg: clearImg
  };
})();
